import { ConnectedPosition } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Injector, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, from, lastValueFrom, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { DateRange } from 'src/app/common/daterange';
import { groupedReports, hasMandatoryCall, isManager, MENU_DEF, MenuDef, MenuItem, MenuNotificationBubble, MenuParent, ReportGroup, VisibleReportsCache } from 'src/app/common/menu-def.provider';
import { CalloutComponent } from 'src/app/components/callout/callout.component';
import { HeaderUpgradePlanDialogComponent } from 'src/app/components/new-billing/header-upgrade-plan-dialog/header-upgrade-plan-dialog.component';
import { getHeaderPaidUpgradeFlow, HEADER_PAID_UPGRADE_CTA_FLAG, HEADER_PAID_UPGRADE_EVENTS, HEADER_PAID_UPGRADE_LINK_LOCATION, HEADER_PAID_UPGRADE_UPSELL_NAME, isHeaderPaidUpgradeVisible } from 'src/app/constants/header-paid-upgrade-cta.constants';
import { UPSELL_SETUP_FLOW_FLAG } from 'src/app/constants/upsell-setup-flow.constants';
import { Severity } from 'src/app/pages/ask-ai/models';
import { AskAiService } from 'src/app/pages/ask-ai/services/ask-ai.service';
import { BillingService } from 'src/app/services/billing/billing.service';
import { UpgradeService } from 'src/app/services/billing/upgrade.service';
import { BrandingService } from 'src/app/services/branding.service';
import { CelloService } from 'src/app/services/cello.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { MeetingInsightsCalendarIntegrationGbValue } from 'src/app/services/meeting-insights/meeting-insights.service';
import { NavatticExperimentService } from 'src/app/services/navattic-experiment.service';
import { NotificationVisitService } from 'src/app/services/notification-visit.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { provideAmpTrackCtxFromUrl } from 'src/app/services/tracking/amp-track';
import { TrialBannerBuyNowExperimentService } from 'src/app/services/trial-banner-buy-now-experiment.service';
import { Logout, SelectCompany } from 'src/app/store/auth/auth.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { environment } from 'src/environments/environment';
import { AuthCompany, AuthUser } from 'src/models';
import { NavatticActionsService } from '../navattic-tutorial/navattic-actions.service';
import { NavatticMenuOpenService } from '../navattic-tutorial/navattic-menu-open.service';
import { NavatticPersistenceService } from '../navattic-tutorial/navattic-persistence.service';
import { helpLinks } from './help-links';

interface MenuItemWithNotificationBubble {
  notificationBubble?: MenuNotificationBubble;
}

type ActiveCallout = 'tdAcademy' | 'tutorialsMoved' | 'bookACall' | null;

/** Applied to the CDK overlay pane (not `.mat-menu-panel`). Kept in sync with `header.component.scss`. */
const COMPANY_SELECT_MENU_OVERLAY_PANEL_CLASS = 'company-select-menu-overlay';

/** GrowthBook: when true, company switcher lives in the avatar menu (GT2-4594); when false, toolbar + legacy profile header. */
const COMPANY_SELECTOR_IN_PROFILE_MENU_GB_KEY = 'company-selector-in-profile-menu';

/**
 * Route the Time Doctor logo (`.home-link`) points at. The Reports > AI Signals item shares this
 * route, but it's just a shortcut to the logo's destination, not a distinct Reports page — so it
 * shouldn't highlight REPORTS the way a real report would. [AA-138]
 */
const HOME_LINK_ROUTE = '/signals';

/**
 * GrowthBook: gates the AI Signals count badge on the Time Doctor logo. Off by default — the count
 * still comes from mock fixtures, so shipping it unflagged would put a fabricated number in front
 * of every customer on every page. [AA-138]
 */
const AI_SIGNALS_BADGE_GB_KEY = 'app-web-ai-signals-badge';

/**
 * GrowthBook: gates the Time Doctor logo's destination itself — the AI Signals/Copilot experience
 * as a whole, not just its count badge (that's AI_SIGNALS_BADGE_GB_KEY, a separate flag). Off by
 * default: the logo falls back to routing at '/', its pre-AA-138 behavior. [AA-138]
 */
const AI_COPILOT_GB_KEY = 'ask-ai-new-signals-page';

/** Above this, the badge reads "9+" so it stays a single glyph wide and never widens the tile. */
const MAX_SIGNALS_BADGE_COUNT = 9;

/** Worst-first, so the AI Signals logo badge takes its colour from the most severe open signal. [AA-138] */
const SEVERITY_RANK: Severity[] = ['critical', 'warning', 'info', 'ai'];

function worstSeverity(severities: Severity[]): Severity {
  return SEVERITY_RANK.find(rank => severities.indexOf(rank) !== -1) ?? 'ai';
}

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [provideAmpTrackCtxFromUrl()],
})
export class HeaderComponent implements AfterViewInit {
  company: AuthCompany;
  @Output() navToggle = new EventEmitter<boolean>();
  @Output() sidebarMenuClick = new EventEmitter<void>();

  @Select(AuthState.authorized) authorized$: Observable<string>;
  @Select(AuthState.user) user$: Observable<AuthUser>;
  @Select(AuthState.isManager) isManager$: Observable<boolean>;
  @Select(AuthState.company) company$: Observable<AuthCompany>;
  @Select(AuthState.companies) companies$: Observable<AuthCompany[]>;
  hasMultipleCompanies: boolean;
  hasMandatoryCall: boolean;
  employeePrivacyDisclaimerUrl = environment.env.TD_APP_EMPLOYEE_PRIVACY_DISCLAIMER;
  contractorPrivacyDisclaimerUrl = environment.env.TD_APP_CONTRACTOR_PRIVACY_DISCLAIMER;
  trainingGuideUrl = environment.env.TD_APP_TRAINING_GUIDE_URL;
  readonly timeDoctorAcademyUrl = 'https://academy.timedoctor.com/home?&utm_campaign=tdacademy&utm_source=product&utm_medium=profile-menu';

  customRouterLinkActiveOptions: IsActiveMatchOptions = { paths: 'exact', fragment: 'exact', queryParams: 'ignored', matrixParams: 'ignored' };

  @Input() activeUrl: string;
  @Input() withNavSidebar = false;

  /**
   * Count + worst severity of the open AI Signals, rendered as a badge on the Time Doctor logo
   * (which navigates to the AI Signals page). Null when there is nothing open, or when the
   * `app-web-ai-signals-badge` flag is off. `label` is the display string, capped at "9+". [AA-138]
   *
   * NOTE: `AskAiService` is still a mock fixture source — no signals backend exists yet — so this
   * count is the same for every user until the threshold engine ships. When it does, only the
   * body of this observable changes; the template and styles stay as they are.
   */
  signalsBadge$: Observable<{ count: number, label: string, sev: Severity } | null>;

  /** Gates whether the Time Doctor logo routes to the AI Signals page at all — false falls back to
   * '/' via #legacyHomeLink in the template. See AI_COPILOT_GB_KEY. [AA-138] */
  copilotEnabled$: Observable<boolean>;

  showTimeDoctorAcademyLink$: Observable<boolean>;
  showAcademyNewBadge$: Observable<boolean>;
  showAcademyCallout$: Observable<boolean>;
  showCelloReferral$: Observable<boolean>;
  private celloReferralAmountCache: string | undefined;

  showRelocatedMenuItems$: Observable<boolean>;
  showTakeTourMenuItem$: Observable<boolean>;
  showBookCallMenuItem$: Observable<boolean>;
  showTakeTourNewBadge$: Observable<boolean>;
  showBookCallNewBadge$: Observable<boolean>;
  showTutorialsMovedCallout$: Observable<boolean>;
  showBookCallMovedCallout$: Observable<boolean>;
  activeCallout$: Observable<ActiveCallout>;
  showBuyNowButton$: Observable<boolean>;
  /** Paid customers only — independent of trial Buy Now experiment (`header-paid-upgrade-cta`). */
  showPaidUpgradeButton$: Observable<boolean>;
  /** When true, company name / switcher is shown in the profile menu instead of the main toolbar. */
  showCompanySelectorInProfileMenu$: Observable<boolean>;

  // Reports menu grouping A/B test
  reportsMenuGroupingVariant: ExperimentProps['variationid'] | null = null;
  showGroupedReports = false;
  groupedReports = groupedReports;
  private reportsMenuExperiment: ExperimentProps | null = null;

  /** Cached top-level visible links — avoid re-running menu GB conditions on every CD. */
  private visibleTopLevelLinks: Set<string> | null = null;
  private visibleTopLevelCacheCompanyId: string | undefined;
  private visibleTopLevelCacheMenuDef: MenuDef | null = null;
  private visibleTopLevelCacheGbReady = false;
  private visibleReportsCache: VisibleReportsCache;

  @ViewChild('academyCallout') academyCallout: CalloutComponent;
  @ViewChild('tutorialsMovedCallout') tutorialsMovedCallout: CalloutComponent;
  @ViewChild('bookCallMovedCallout') bookCallMovedCallout: CalloutComponent;
  @ViewChild('profileMenuTrigger') profileMenuTrigger: MatMenuTrigger;
  @ViewChild('companySelectMenu') companySelectMenu: MatMenu;
  @ViewChild('celloReferralButton') celloReferralButton: any;

  private readonly academyLinkClickedNotification = 'academy-menu-link-clicked';
  private readonly academyCalloutDismissedNotification = 'academy--callout-dismissed';

  private readonly academyCalloutDismissed$: BehaviorSubject<boolean>;

  readonly tutorialsMovedCalloutTitle = 'header.tutorialsMovedCallout.title';
  readonly tutorialsMovedCalloutDescription = 'header.tutorialsMovedCallout.description';
  readonly bookCallMovedCalloutTitle = 'header.bookCallMovedCallout.title';
  readonly bookCallMovedCalloutDescription = 'header.bookCallMovedCallout.description';

  /** Profile avatar callouts stay below the avatar; fall back above when there is no room below. */
  readonly profileCalloutPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  get academyCalloutId(): string {
    return this.academyCalloutDismissedNotification;
  }

  get academyLinkClickedId(): string {
    return this.academyLinkClickedNotification;
  }

  meetingInsightsMyAccountIntegration$: Observable<boolean>;

  constructor(
    private readonly store: Store,
    @Inject(MENU_DEF) public menuDef: MenuDef,
    public branding: BrandingService,
    public injector: Injector,
    private readonly growthBook: GrowthBookService,
    private readonly askAiService: AskAiService,
    private readonly notificationVisitService: NotificationVisitService,
    private readonly navatticPersistence: NavatticPersistenceService,
    private readonly navatticMenuOpen: NavatticMenuOpenService,
    private readonly navatticActions: NavatticActionsService,
    private readonly navatticExperimentService: NavatticExperimentService,
    private readonly trialBannerBuyNowExperiment: TrialBannerBuyNowExperimentService,
    private readonly celloService: CelloService,
    private readonly router: Router,
    private readonly segmentService: SegmentService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly matDialog: MatDialog,
    private readonly upgradeService: UpgradeService,
    private readonly billingService: BillingService,
  ) {
    this.visibleReportsCache = new VisibleReportsCache(this.injector);

    this.showPaidUpgradeButton$ = combineLatest([
      this.growthBook.getFeature<boolean>(HEADER_PAID_UPGRADE_CTA_FLAG, false),
      this.company$,
      this.authorized$,
    ]).pipe(
      switchMap(([flagOn, company, isAuthorized]) => {
        if (!flagOn || !isAuthorized || !company) {
          return of(false);
        }
        const isPaid = company.subscription?.status === 'paid';
        const hasBillingAccess = company.role === 'owner' || !!company.userSettings?.billingAccess;
        if (!isPaid || !hasBillingAccess) {
          return of(false);
        }
        // Use live currentPlanId so Premium + scheduled Basic doesn't show a no-op Upgrade CTA.
        return this.billingService.getBillingDetails().pipe(
          map((details) => isHeaderPaidUpgradeVisible(flagOn, company, isAuthorized, details?.currentPlanId)),
          catchError(() => of(isHeaderPaidUpgradeVisible(flagOn, company, isAuthorized))),
        );
      }),
      distinctUntilChanged(),
    );

    // Trial Buy Now and paid Upgrade are mutually exclusive; paid path wins when eligible.
    this.showBuyNowButton$ = combineLatest([
      this.trialBannerBuyNowExperiment.showBuyNowButton$,
      this.showPaidUpgradeButton$,
    ]).pipe(
      map(([showTrialBuyNow, showPaidUpgrade]) => showTrialBuyNow && !showPaidUpgrade),
      distinctUntilChanged(),
    );

    this.companies$.subscribe((companiesList) => {
      if (companiesList) {
        this.hasMultipleCompanies = companiesList.length > 1;
      } else {
        this.hasMultipleCompanies = false;
      }
    });
    this.company$
      .pipe(untilDestroyed(this))
      .subscribe((company) => {
        this.company = company;
        this.hasMandatoryCall = hasMandatoryCall(company);
        this.visibleTopLevelLinks = null;
      });

    combineLatest([
      this.growthBook.initialized.pipe(filter(Boolean), distinctUntilChanged()),
      this.growthBook.getFeature<boolean>(UPSELL_SETUP_FLOW_FLAG, false),
      this.growthBook.getFeature<boolean>('schedules:upsell', false),
    ]).pipe(untilDestroyed(this)).subscribe(() => {
      this.visibleTopLevelLinks = null;
      this.cdr.markForCheck();
    });

    this.showTimeDoctorAcademyLink$ = this.growthBook.getFeature<boolean>('time-doctor-academy-link', false);

    // Check both the callout's storage key and the visited notification key
    // Callout uses "callout{id}" key, header uses "visited-{notif}" key
    const calloutStorageKey = `callout${this.academyCalloutDismissedNotification}`;
    const isDismissedInCalloutStorage = (() => {
      try {
        return localStorage.getItem(calloutStorageKey) === 'false';
      } catch (e) {
        return false;
      }
    })();
    const isDismissedInVisitedStorage = this.notificationVisitService.visitedNotification(this.academyCalloutDismissedNotification);
    this.academyCalloutDismissed$ = new BehaviorSubject<boolean>(
      isDismissedInCalloutStorage || isDismissedInVisitedStorage,
    );

    this.showAcademyNewBadge$ = combineLatest([
      this.showTimeDoctorAcademyLink$,
      this.company$,
    ]).pipe(
      map(([academyEnabled]) => academyEnabled &&
        !this.notificationVisitService.visitedNotification(this.academyLinkClickedNotification)),
    );

    const company$ = this.store.select(AuthState.company);
    const isActiveVariantB$ = this.navatticExperimentService.isVariant$('B');
    const activeTutorial$ = this.navatticExperimentService.getActiveTutorial$('B');

    const growthBookInitialized$ = this.growthBook.initialized.pipe(
      filter(initialized => initialized),
      distinctUntilChanged(),
    );

    // Open-signal count for the logo badge. Deliberately an OPEN count, not an unread one: nothing
    // in the ask-ai feature persists yet, so there is nowhere to record "seen" and no acknowledge
    // model to honour. Reads the same default period the page opens on (`w0`), so on a fresh load
    // the badge agrees with the page's own "Needs you" count. It does not follow the page's date or
    // user pickers on purpose — those are transient view state, and a count scoped to them would be
    // unreadable from another page. In-session dismissals are not reflected (they don't survive a
    // reload either); wire this to the real engine's count when the threshold backend ships. [AA-138]
    // Last completed week — the same default AskAiScopeService seeds its own dateRange$ with, so
    // this reads real live data for the same period (w0) the Ask AI page itself opens on, rather
    // than always falling through to the SIGNALS fixture.
    const badgeWeek = DateRange.byType('week', DateTime.utc().minus({ week: 1 }));
    const { from: badgeFrom, to: badgeTo } = badgeWeek.asApiParams();

    this.signalsBadge$ = combineLatest([
      this.growthBook.getFeature<boolean>(AI_SIGNALS_BADGE_GB_KEY, false),
      growthBookInitialized$,
    ]).pipe(
      switchMap(([enabled]) => enabled !== true
        ? of(null)
        : from(this.badgeSeverities(badgeFrom, badgeTo, badgeWeek.autoFormat())).pipe(
          map(severities => severities.length
            ? {
              count: severities.length,
              label: severities.length > MAX_SIGNALS_BADGE_COUNT ? `${MAX_SIGNALS_BADGE_COUNT}+` : `${severities.length}`,
              sev: worstSeverity(severities),
            }
            : null),
        )),
      catchError(() => of(null)),
    );

    const fabMoveToAvatarMenu$ = combineLatest([
      this.growthBook.getFeature<boolean>('fab-move-to-avatar-menu', false),
      growthBookInitialized$,
    ]).pipe(
      map(([fabMoveToAvatarMenu]) => fabMoveToAvatarMenu === true),
      distinctUntilChanged(),
    );

    this.showCompanySelectorInProfileMenu$ = combineLatest([
      this.growthBook.getFeature<boolean>(COMPANY_SELECTOR_IN_PROFILE_MENU_GB_KEY, false),
      growthBookInitialized$,
    ]).pipe(
      map(([inProfileMenu]) => inProfileMenu === true),
      distinctUntilChanged(),
    );

    this.copilotEnabled$ = combineLatest([
      this.growthBook.getFeature<boolean>(AI_COPILOT_GB_KEY, false),
      growthBookInitialized$,
    ]).pipe(
      map(([enabled]) => enabled === true),
      distinctUntilChanged(),
    );

    this.meetingInsightsMyAccountIntegration$ = combineLatest([
      this.company$,
      this.growthBook.getFeature<MeetingInsightsCalendarIntegrationGbValue>('meeting_insights_v2_google_calendar', null),
      this.growthBook.getFeature<boolean>('meeting-insights-my-account-integration', false),
      growthBookInitialized$,
    ]).pipe(
      map(([company, calendarIntegrationGbValue, myAccountIntegration]) => {
        if (!company) return false;

        const outlookIntegration = calendarIntegrationGbValue?.showMyAccountOutlookIntegration ?? false;

        const allowGC = company.companySettings?.allowGoogleCalendarIntegration;
        const allowRegularUsersGC = company.companySettings?.allowRegularUsersGCIntegration;
        const allowOutlook = company.companySettings?.allowOutlookCalendarIntegration;
        const allowRegularUsersOutlook = company.companySettings?.allowRegularUsersOutlookIntegration;

        const isManagerOrAdmin = isManager(company);
        const allowUsersGC = (company.role === 'user' && allowRegularUsersGC) || isManagerOrAdmin;
        const allowUsersOutlook = (company.role === 'user' && allowRegularUsersOutlook) || isManagerOrAdmin;

        const showGC = !!(myAccountIntegration && allowGC && allowUsersGC);
        const showOutlook = !!(outlookIntegration && allowOutlook && allowUsersOutlook);

        return showGC || showOutlook;
      }),
      distinctUntilChanged(),
    );

    const eligibleStartTutorial$ = combineLatest([
      activeTutorial$,
      growthBookInitialized$,
    ]).pipe(
      map(([activeTutorial]) => !!activeTutorial),
      distinctUntilChanged(),
    );

    this.showTakeTourMenuItem$ = combineLatest([
      eligibleStartTutorial$,
      fabMoveToAvatarMenu$,
    ]).pipe(
      map(([eligible, fabMoveToAvatarMenu]) => {
        return fabMoveToAvatarMenu && eligible;
      }),
      distinctUntilChanged(),
    );

    const eligibleBookCall$ = combineLatest([
      isActiveVariantB$,
      this.growthBook.getFeature('book-call-sales-v2', false),
      company$,
      growthBookInitialized$,
    ]).pipe(
      map(([isVariantB, hasBookACallAccess, company]) => {
        return !!company && isVariantB && hasBookACallAccess;
      }),
      distinctUntilChanged(),
    );

    this.showBookCallMenuItem$ = combineLatest([
      eligibleBookCall$,
      fabMoveToAvatarMenu$,
    ]).pipe(
      map(([eligible, fabMoveToAvatarMenu]) => {
        return fabMoveToAvatarMenu && eligible;
      }),
      distinctUntilChanged(),
    );

    this.showRelocatedMenuItems$ = combineLatest([
      this.showTakeTourMenuItem$,
      this.showBookCallMenuItem$,
    ]).pipe(
      map(([showTakeTour, showBookCall]) => showTakeTour || showBookCall),
      distinctUntilChanged(),
    );

    this.showTakeTourNewBadge$ = combineLatest([
      this.showTakeTourMenuItem$,
      this.navatticPersistence.showTakeTourNewBadge$(),
    ]).pipe(
      map(([showItem, showBadge]) => showItem && showBadge),
      distinctUntilChanged(),
    );

    this.showBookCallNewBadge$ = combineLatest([
      this.showBookCallMenuItem$,
      this.navatticPersistence.showBookCallNewBadge$(),
    ]).pipe(
      map(([showItem, showBadge]) => showItem && showBadge),
      distinctUntilChanged(),
    );

    const academyEligible$ = combineLatest([
      this.showTimeDoctorAcademyLink$,
      this.academyCalloutDismissed$,
    ]).pipe(
      map(([academyEnabled, calloutDismissed]) => academyEnabled && !calloutDismissed),
      distinctUntilChanged(),
    );

    const tutorialsCalloutDismissed$ = this.navatticPersistence.isCalloutDismissed$();
    const bookCallMovedCalloutDismissed$ = this.navatticPersistence.isBookCallMovedCalloutDismissed$();

    // Check if user is paid - callouts should only show after upgrade to paid status
    const isPaid$ = company$.pipe(
      map((company) => company?.subscription?.status === 'paid'),
      distinctUntilChanged(),
    );

    const tutorialsMovedEligible$ = combineLatest([
      this.showTakeTourMenuItem$,
      tutorialsCalloutDismissed$,
      isPaid$,
      fabMoveToAvatarMenu$,
    ]).pipe(
      map(([showTakeTour, calloutDismissed, isPaid, fabMoveToAvatarMenu]) => {
        // Callouts show when: menu item visible AND not dismissed AND user is paid AND feature flag enabled
        // Note: showTakeTourMenuItem$ already requires fabMoveToAvatarMenu$, but we check it explicitly here
        // to ensure callouts only show when the feature flag is enabled
        return showTakeTour && !calloutDismissed && isPaid && fabMoveToAvatarMenu;
      }),
      distinctUntilChanged(),
    );

    const bookCallMovedEligible$ = combineLatest([
      this.showBookCallMenuItem$,
      bookCallMovedCalloutDismissed$,
      isPaid$,
      fabMoveToAvatarMenu$,
    ]).pipe(
      map(([showBookCall, bookCallDismissed, isPaid, fabMoveToAvatarMenu]) => {
        // Callouts show when: menu item visible AND not dismissed AND user is paid AND feature flag enabled
        // Note: showBookCallMenuItem$ already requires fabMoveToAvatarMenu$, but we check it explicitly here
        // to ensure callouts only show when the feature flag is enabled
        return showBookCall && !bookCallDismissed && isPaid && fabMoveToAvatarMenu;
      }),
      distinctUntilChanged(),
    );

    this.activeCallout$ = this.getActiveCallout$(
      academyEligible$,
      tutorialsMovedEligible$,
      bookCallMovedEligible$,
    );

    this.showAcademyCallout$ = this.activeCallout$.pipe(
      map(activeCallout => activeCallout === 'tdAcademy'),
      distinctUntilChanged(),
    );

    this.showTutorialsMovedCallout$ = this.activeCallout$.pipe(
      map(activeCallout => activeCallout === 'tutorialsMoved'),
      distinctUntilChanged(),
    );

    this.showBookCallMovedCallout$ = this.activeCallout$.pipe(
      map(activeCallout => activeCallout === 'bookACall'),
      distinctUntilChanged(),
    );

    this.navatticMenuOpen.openMyAccountMenu$.pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      this.profileMenuTrigger?.openMenu();
    });

    // Feature flag for Cello referral (optional - can be removed if always enabled)
    this.showCelloReferral$ = this.growthBook.getFeature<boolean>('cello-referral-enabled', false);


    this.growthBook.getFeature<ExperimentProps>('reports-menu-grouping-experiment')
      .pipe(untilDestroyed(this))
      .subscribe((experiment: ExperimentProps | null) => {
        const isValidVariation = experiment?.variationid === 'A' || experiment?.variationid === 'B';
        this.reportsMenuGroupingVariant = isValidVariation ? experiment.variationid : null;
        this.showGroupedReports = isValidVariation;
        this.reportsMenuExperiment = isValidVariation ? experiment : null;
      });

  }

  /** The logo badge's open-signal severities. Uses person signal cards when
   * AskAiService.personCardsEnabled (AA-214), matching whichever view Needs You is showing,
   * otherwise the regular signals (see AskAiState.fetchSignalsOrPersonCards for the same branch). */
  private async badgeSeverities(dateFrom: string, dateTo: string, label: string): Promise<Severity[]> {
    const signals = this.askAiService.personCardsEnabled
      ? await this.askAiService.getPersonSignalCards(dateFrom, dateTo, label, await this.askAiService.getAlertTypes())
      // periods[0].key is the fallback period if getPeriods fails to return one. getSignals now
      // also returns precomputed personWeeks (AA-247); the badge only needs the signal severities.
      : (await this.askAiService.getSignals(dateFrom, dateTo, label, (await this.askAiService.getPeriods())[0]?.key)).signals;
    return signals.map(signal => signal.sev);
  }

  get celloReferralButtonText(): string {
    this.loadCelloReferralAmount();
    return this.celloReferralAmountCache;
  }

  ngAfterViewInit() {
    this.showCompanySelectorInProfileMenu$.pipe(
      untilDestroyed(this),
    ).subscribe(showInProfileMenu => {
      if (this.companySelectMenu) {
        // Submenu-from-profile positioning uses `transform` on `.cdk-overlay-pane`; navbar-only mode does not need it.
        this.companySelectMenu.overlayPanelClass = showInProfileMenu ? COMPANY_SELECT_MENU_OVERLAY_PANEL_CLASS : '';
      }
    });
    // Initialize Cello after view is ready
    this.initializeCello();
  }

  navOpen() {
    this.navToggle.emit(true);
  }

  logout() {
    this.store.dispatch(new Logout(true));
  }

  async selectCompany(company: AuthCompany) {
    await lastValueFrom(this.store.dispatch(new SelectCompany(company)));
  }

  anyChildActive(item: MenuParent) {
    const isBillingPage = item.name === 'header.settings' && this.activeUrl?.includes('billing');
    const activePath = '/' + this.activeUrl;
    const topLevelVisible = this.getVisibleTopLevelLinks();
    return item.children.some(x =>
      (x['routerLink'] === activePath
        && x['routerLink'] !== HOME_LINK_ROUTE
        && !topLevelVisible.has(x['routerLink'])) || isBillingPage,
    );
  }

  private getVisibleTopLevelLinks(): Set<string> {
    const gbReady = this.growthBook.initialized.value;
    if (
      this.visibleTopLevelLinks
      && this.visibleTopLevelCacheCompanyId === this.company?.id
      && this.visibleTopLevelCacheMenuDef === this.menuDef
      && this.visibleTopLevelCacheGbReady === gbReady
    ) {
      return this.visibleTopLevelLinks;
    }

    const links = new Set<string>();
    for (const item of this.menuDef.items) {
      if ((item as MenuParent).children) continue;
      const routerLink = item['routerLink'];
      if (!routerLink) continue;
      if (!item.condition || item.condition(this.company, this.injector)) {
        links.add(routerLink);
      }
    }

    this.visibleTopLevelLinks = links;
    this.visibleTopLevelCacheCompanyId = this.company?.id;
    this.visibleTopLevelCacheMenuDef = this.menuDef;
    this.visibleTopLevelCacheGbReady = gbReady;
    return links;
  }

  getHelpLinks() {
    return this.branding.isDefault
      ? helpLinks[this.activeUrl] || []
      : [];
  }

  helpButtonEnabled() {
    return this.employeePrivacyDisclaimerUrl
      || this.contractorPrivacyDisclaimerUrl
      || this.trainingGuideUrl
      || this.helpCenterEnabled()
      || this.contactUsEnabled();
  }

  onBuyNow(_event: MouseEvent): void {
    const currentPath = this.router.url;
    this.router.navigate(['/new-billing'], { queryParams: { choosePlan: 'yes', url: currentPath } });
  }

  onPaidUpgrade(_event: MouseEvent): void {
    const returnUrl = this.router.url;
    this.billingService.getBillingDetails().pipe(
      take(1),
      map((details) => ({
        flow: getHeaderPaidUpgradeFlow(this.company, details?.currentPlanId),
        currentPlanId: details?.currentPlanId ?? null,
        billingFetchFailed: false,
      })),
      catchError(() => of({
        flow: getHeaderPaidUpgradeFlow(this.company),
        currentPlanId: null,
        billingFetchFailed: true,
      })),
    ).subscribe(({ flow, currentPlanId, billingFetchFailed }) => {
      this.segmentService.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.flowStarted, {
        flow: flow ?? 'none',
        currentplanid: currentPlanId,
        billingfetchfailed: billingFetchFailed,
        linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
      });

      if (flow === 'premium-confirm') {
        // Standard → Payment Summary v2 with Premium preselected
        this.upgradeService.initiatePlanUpgrade('premium', returnUrl, {
          trackingContext: {
            upsellname: HEADER_PAID_UPGRADE_UPSELL_NAME,
            linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
          },
        }).subscribe({
          error: () => {
            this.segmentService.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.flowFailed, {
              flow,
              stage: 'payment-summary',
              fallback: 'choose-plan-page',
              linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
            });
            void this.router.navigate(['/new-billing'], {
              queryParams: { choosePlan: 'yes', url: returnUrl },
            });
          },
        });
        return;
      }

      if (flow !== 'choose-plan') {
        return;
      }

      // Basic → plan picker, then Payment Summary v2 for the selected plan
      this.matDialog.open(HeaderUpgradePlanDialogComponent, {
        data: { returnUrl },
        autoFocus: false,
        disableClose: true, // Same as Confirm plan upgrade — Cancel or Continue only
        panelClass: 'header-upgrade-plan-dialog-panel',
        width: '584px',
        maxWidth: '95vw',
      });
    });
  }

  helpCenterEnabled() {
    return this.growthBook.getFeatureValue<boolean>('header-help-links', false)
      || this.growthBook.getFeatureValue<boolean>('help-center-url', false);
  }

  contactUsEnabled() {
    return this.growthBook.getFeatureValue<boolean>('contact-us', true);
  }

  openBubbleMessage() {
    const formUrl = this.growthBook.getFeatureValue<string>('contact-us-form-url', '');
    if (formUrl) {
      window.open(formUrl, '_blank');
      return;
    }
    window.HubSpotConversations?.widget?.open();
  }

  shouldShowNotificationBubble(item: MenuItemWithNotificationBubble, company: AuthCompany): boolean {
    if (!item?.notificationBubble) return false;

    const bubble = item.notificationBubble;
    if (!bubble?.condition) return false;

    return bubble.condition(company, this.injector);
  }

  onNotificationBubbleDismiss(bubbleId: string) {
    this.notificationVisitService.setNotificationVisit(bubbleId);
  }

  getNotificationBubbleTitle(item: MenuItemWithNotificationBubble): string {
    const bubble = item?.notificationBubble;
    if (!bubble?.title) return '';

    if (typeof bubble.title === 'function') {
      return bubble.title(this.injector);
    }

    return bubble.title;
  }

  getNotificationBubbleDescription(item: MenuItemWithNotificationBubble): string {
    const bubble = item?.notificationBubble;
    if (!bubble?.description) return '';

    if (typeof bubble.description === 'function') {
      return bubble.description(this.injector);
    }

    return bubble.description;
  }

  getNotificationBubbleCtaButtonLabel(item: MenuItemWithNotificationBubble): string {
    const bubble = item?.notificationBubble;
    if (!bubble?.ctaButtonLabel) return '';

    if (typeof bubble.ctaButtonLabel === 'function') {
      return bubble.ctaButtonLabel(this.injector);
    }

    return bubble.ctaButtonLabel;
  }

  onAcademyCalloutDismiss = () => {
    this.academyCallout?.dismissCallout();
    // Note: dismissCallout() saves to localStorage with key "callout{id}" when saveVisitByDB is false
    // We also need to update localStorage with the key that the BehaviorSubject checks
    // Only "take a tour" and "book a call" callouts persist in the database
    try {
      localStorage.setItem(`visited-${this.academyCalloutDismissedNotification}`, 'true');
    } catch (e) {
      console.error('Failed to write to localStorage:', e);
    }
    this.academyCalloutDismissed$.next(true);
    this.profileMenuTrigger?.openMenu();
  };

  onTutorialsMovedCalloutDismiss = (injector: Injector) => {
    this.tutorialsMovedCallout?.dismissCallout();
    this.navatticPersistence.dismissCallout();
    this.navatticMenuOpen.openMenu();
  };

  onBookCallMovedCalloutDismiss = (injector: Injector) => {
    this.bookCallMovedCallout?.dismissCallout();
    this.navatticPersistence.dismissBookCallMovedCallout();
    this.navatticMenuOpen.openMenu();
  };

  onTakeTourMenuClick(): void {
    this.navatticPersistence.dismissTakeTourNewBadge();
    this.navatticActions.startTutorial();
  }

  onBookCallMenuClick(): void {
    this.navatticPersistence.dismissBookCallNewBadge();
    this.navatticActions.bookCall();
  }

  private getActiveCallout$(
    tdAcademyEligible$: Observable<boolean>,
    tutorialsMovedEligible$: Observable<boolean>,
    bookACallEligible$: Observable<boolean>,
  ): Observable<ActiveCallout> {
    return combineLatest([
      tdAcademyEligible$,
      tutorialsMovedEligible$,
      bookACallEligible$,
    ]).pipe(
      map(([tdAcademyEligible, tutorialsMovedEligible, bookACallEligible]): ActiveCallout => {
        if (tdAcademyEligible) return 'tdAcademy';
        if (tutorialsMovedEligible) return 'tutorialsMoved';
        if (bookACallEligible) return 'bookACall';
        return null;
      }),
      distinctUntilChanged(),
    );
  }

  private initializeCello() {
    // Wait for user, company, AND feature flag to be available
    // This ensures initialization happens when the feature is enabled,
    // regardless of DOM timing issues
    combineLatest([
      this.user$.pipe(filter(user => !!user)),
      this.company$.pipe(filter(company => !!company)),
      this.showCelloReferral$.pipe(filter(enabled => enabled === true)),
    ]).pipe(
      take(1),
      untilDestroyed(this),
    ).subscribe(async ([user, company]) => {
      // Wait a bit for DOM to be fully ready
      setTimeout(async () => {
        try {
          await this.celloService.initialize(user, company);
          await this.loadCelloReferralAmount();
        } catch (error) {
          console.error('Failed to initialize Cello:', error);
        }
      }, 500);
    });
  }

  private async loadCelloReferralAmount(): Promise<void> {
    if (!this.celloService.isInitialized()) {
      return;
    }
    if (this.celloReferralAmountCache) {
      return;
    }

    try {
      const labels = await this.celloService.getLabels();
      this.celloReferralAmountCache = labels.customLauncher;
    } catch (error) {
      console.error('Failed to get Cello labels:', error);
    }
  }

  openCelloReferral(): void {
    if (this.celloService.isInitialized()) {
      this.celloService.show();
    } else {
      // If not initialized yet, try to initialize and then show
      combineLatest([
        this.user$.pipe(filter(user => !!user)),
        this.company$.pipe(filter(company => !!company)),
      ]).pipe(
        take(1),
        untilDestroyed(this),
      ).subscribe(async ([user, company]) => {
        try {
          await this.celloService.initialize(user, company);
          // Small delay to ensure initialization completes
          setTimeout(() => {
            this.celloService.show();
          }, 100);
        } catch (error) {
          console.error('Failed to initialize Cello:', error);
        }
      });
    }
  }

  // Called from template expressions, so this runs on every change-detection pass.
  // The cache keeps both the flag evaluations and the array identity stable.
  getVisibleReports(group: ReportGroup, company: AuthCompany): MenuItem[] {
    return this.visibleReportsCache.get(group, company);
  }

  hasVisibleReports(group: ReportGroup, company: AuthCompany): boolean {
    return this.getVisibleReports(group, company).length > 0;
  }

  /**
   * Get report group by stable key identifier
   */
  getGroupByKey(key: string): ReportGroup | undefined {
    return this.groupedReports.find(g => g.key === key);
  }

  /**
   * Get panel class for Reports menu based on variant
   */
  getReportsMenuPanelClass(item: MenuItem | MenuParent): string {
    if (item.name !== 'header.reports' || !this.showGroupedReports) {
      return '';
    }
    return this.reportsMenuGroupingVariant === 'B'
      ? 'reports-menu-grouped-panel reports-menu-variant-b-panel'
      : 'reports-menu-grouped-panel reports-menu-variant-a-panel';
  }

  /**
   * Track experiment view when Reports menu is clicked
   */
  onMenuButtonClick(item: MenuItem | MenuParent): void {
    if (item.name === 'header.reports') {
      // Track Reports menu dropdown click
      this.segmentService.trackInAppEvents('Reports Main Menu Dropdown Clicked', {
        menuclickedlocation: document.title,
        menuclickedurl: window.location.href,
      });

      // Track experiment view if user is in the experiment
      if (this.reportsMenuExperiment) {
        const variationName = this.reportsMenuExperiment.variationid === 'A'
          ? 'Grouped Reports menu - vertical layout'
          : 'Grouped Reports menu - horizontal layout';

        this.segmentService.trackInAppEvents('Experiment Viewed', {
          experimentid: 'CF-STAFF-144644',
          experimentname: 'Reports Menu Grouping Experiment',
          variationid: this.reportsMenuExperiment.variationid,
          variationname: variationName,
        });
      }
    }
  }

  /**
   * Track when a report menu item is clicked
   */
  onReportItemClick(report: MenuItem, group: ReportGroup): void {
    const reportName = this.translate.instant(report.name);
    const groupName = this.translate.instant(group.name);

    this.segmentService.trackInAppEvents('Reports Menu Item Clicked', {
      reportitem: reportName,
      reportgroup: groupName,
      menuclickedlocation: document.title,
      menuclickedurl: window.location.href,
    });
  }
}
